import { getClient, Components, type Client } from '@epilot/metering-client'

import { previewProxyAdapter } from 'dummy/proxy-adapter'
import AuthUtils from 'utils/AuthUtils'

import { getConfig } from '../EnvConfig'

export interface Address {
  _id: string
  street: string
  street_number: string
  city: string
  postal_code: string
  country: string
  additional_info?: string
  _tags: string[]
}

export type Meter = Components.Schemas.Meter & { address: Address[] }
export type MeterCounter = Components.Schemas.MeterCounter
export type MeterReading = Components.Schemas.MeterReading

export enum ReadingDirection {
  FEED_IN = 'feed-in',
  FEED_OUT = 'feed-out'
}

let meteringApiClient: Client | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let originalMeteringAdapter: any = null

export const getMeteringApiClient = () => {
  if (meteringApiClient) return meteringApiClient

  meteringApiClient = getClient()

  meteringApiClient.defaults.baseURL = getConfig().METERING_API_URL

  if (meteringApiClient.interceptors.request['handlers'].length > 3) {
    console.error(
      'Sanity check: This client already has more than 3 request interceptors, refusing to add more!',
      {
        baseURL: meteringApiClient.defaults.baseURL,
        interceptors: meteringApiClient.interceptors
      }
    )
  } else {
    meteringApiClient.interceptors.request.use(async (request) => {
      const token: string = await AuthUtils.getAccessToken()

      if (token) {
        request.headers['authorization'] = `Bearer ${token}`
      }

      return request
    })
  }

  return meteringApiClient
}

/**
 * Install the preview proxy adapter on the metering API client.
 * All requests will be routed through the builder's caching proxy via postMessage.
 */
export function installMeteringPreviewProxyAdapter(): void {
  if (originalMeteringAdapter !== null) return // already installed

  const client = getMeteringApiClient()

  originalMeteringAdapter = client.defaults.adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client.defaults.adapter = previewProxyAdapter as any
}

/**
 * Remove the preview proxy adapter and restore the original adapter.
 */
export function removeMeteringPreviewProxyAdapter(): void {
  if (originalMeteringAdapter === null) return

  const client = getMeteringApiClient()

  client.defaults.adapter = originalMeteringAdapter
  originalMeteringAdapter = null
}
